import { useRef, useState } from 'react';
import { GripVertical, Minus, Check } from 'lucide-react';
import { TODO_COLORS } from '@/lib/todoColors';
import type { TodoCategory, TodoTask } from '@/hooks/useTodos';

interface Props {
  category: TodoCategory;
  width?: number;
  taskAreaHeight?: number;
  onMinimize: () => void;
  onToggleTask: (id: string) => void;
  onAddTask: (content: string) => void;
  onEditTask: (id: string, content: string) => void;
}

export function PostitNote({ category, width = 264, taskAreaHeight = 260, onMinimize, onToggleTask, onAddTask, onEditTask }: Props) {
  const colors = TODO_COLORS[category.color] ?? TODO_COLORS.yellow;

  const [addingText, setAddingText] = useState('');
  const [adding, setAdding]         = useState(false);
  const [editingId, setEditingId]   = useState<string | null>(null);
  const [editText, setEditText]     = useState('');

  const addInputRef  = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  function startAdding() {
    setAdding(true);
    setTimeout(() => addInputRef.current?.focus(), 0);
  }

  function commitAdd() {
    if (addingText.trim()) onAddTask(addingText.trim());
    setAddingText('');
    setAdding(false);
  }

  function startEdit(task: TodoTask) {
    setEditingId(task.id);
    setEditText(task.content);
    setTimeout(() => editInputRef.current?.focus(), 0);
  }

  function commitEdit(id: string) {
    if (editText.trim()) onEditTask(id, editText.trim());
    setEditingId(null);
  }

  return (
    <div
      style={{
        backgroundColor: colors.bg,
        borderRadius: '3px 3px 4px 14px',
        boxShadow: '2px 4px 14px rgba(0,0,0,.22)',
        width,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Canto dobrado */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0,
        width: 0, height: 0,
        borderStyle: 'solid',
        borderWidth: '0 0 18px 18px',
        borderColor: `transparent transparent ${colors.edge} transparent`,
        zIndex: 1,
        pointerEvents: 'none',
      }} />

      {/* Cabeçalho — alça de arrasto */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '7px 8px 5px', cursor: 'grab', color: colors.ink }}>
        <GripVertical size={14} style={{ opacity: 0.45, flexShrink: 0 }} />
        <span style={{
          flex: 1, fontSize: 12, fontWeight: 700, color: colors.ink,
          userSelect: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {category.name}
        </span>
        <button
          onPointerDownCapture={(e) => e.stopPropagation()}
          onClick={onMinimize}
          title="Minimizar"
          style={{
            flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer',
            padding: 2, color: colors.ink, opacity: 0.7, display: 'flex', alignItems: 'center',
          }}
        >
          <Minus size={14} />
        </button>
      </div>

      {/* Lista de tarefas */}
      <div
        onPointerDownCapture={(e) => e.stopPropagation()}
        style={{ flex: 1, overflowY: 'auto', height: taskAreaHeight, padding: '2px 10px' }}
      >
        {category.tasks.length === 0 && (
          <p style={{ fontSize: 11, color: `${colors.ink}70`, margin: '4px 0 8px', fontStyle: 'italic' }}>
            Nenhuma tarefa ainda.
          </p>
        )}
        {category.tasks.map((task) => (
          <div key={task.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '3px 0' }}>
            <button
              onClick={() => onToggleTask(task.id)}
              style={{
                flexShrink: 0, width: 14, height: 14, marginTop: 1, borderRadius: 3,
                border: `1.5px solid ${colors.ink}55`,
                background: task.done ? colors.ink : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
              }}
            >
              {task.done && <Check size={9} color={colors.bg} strokeWidth={3} />}
            </button>

            {editingId === task.id ? (
              <input
                ref={editInputRef}
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onBlur={() => commitEdit(task.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter')  commitEdit(task.id);
                  if (e.key === 'Escape') setEditingId(null);
                }}
                style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', fontSize: 12, color: colors.ink }}
              />
            ) : (
              <span
                onDoubleClick={() => startEdit(task)}
                style={{
                  flex: 1, fontSize: 12, color: colors.ink,
                  textDecoration: task.done ? 'line-through' : 'none',
                  opacity: task.done ? 0.5 : 1,
                  cursor: 'text', wordBreak: 'break-word', lineHeight: 1.4,
                }}
              >
                {task.content}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Rodapé — adicionar tarefa */}
      <div onPointerDownCapture={(e) => e.stopPropagation()} style={{ padding: '6px 10px 18px' }}>
        {adding ? (
          <input
            ref={addInputRef}
            value={addingText}
            placeholder="Nova tarefa…"
            onChange={(e) => setAddingText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter')  commitAdd();
              if (e.key === 'Escape') { setAdding(false); setAddingText(''); }
            }}
            onBlur={commitAdd}
            style={{
              width: '100%', background: 'transparent',
              border: 'none', borderBottom: `1px solid ${colors.ink}40`,
              outline: 'none', fontSize: 12, color: colors.ink, padding: '2px 0',
            }}
          />
        ) : (
          <button onClick={startAdding} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: `${colors.ink}80`, padding: 0 }}>
            + Adicionar tarefa
          </button>
        )}
      </div>
    </div>
  );
}
